import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  ExternalLink,
  Eye,
  Loader2,
  RefreshCw,
  Trash2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { attachmentDisplayName, normalizeFileUrls } from "@/lib/leadAttachments";

type Lead = {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  primary_service: string;
  boat_location: string;
  hear_about: string;
  boat_name: string | null;
  vessel_length: number | null;
  boat_make_model: string | null;
  message: string | null;
  file_urls: any;
  webhook_status: string;
  ip: string | null;
  ua: string | null;
};

type SignedAttachment = { path: string; url: string; name: string };

const Leads = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [signedAttachments, setSignedAttachments] = useState<SignedAttachment[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  // Mint short-lived signed URLs whenever a lead detail dialog opens.
  // Bucket is private, so we cannot rely on stored URLs.
  useEffect(() => {
    let cancelled = false;
    const loadAttachments = async () => {
      if (!selectedLead) {
        setSignedAttachments([]);
        setAttachmentError(null);
        return;
      }
      const paths = normalizeFileUrls(selectedLead.file_urls);
      if (paths.length === 0) {
        setSignedAttachments([]);
        setAttachmentError(null);
        return;
      }
      setAttachmentsLoading(true);
      setAttachmentError(null);
      try {
        const { data, error } = await supabase.storage
          .from("lead-attachments")
          .createSignedUrls(paths, 60 * 60); // 1 hour

        if (error) throw error;
        if (cancelled) return;

        const resolved: SignedAttachment[] = (data ?? [])
          .map((entry, idx) => ({
            path: paths[idx],
            url: entry.signedUrl ?? "",
            name: attachmentDisplayName(paths[idx]),
            error: entry.error,
          }))
          .filter((a) => a.url)
          .map(({ path, url, name }) => ({ path, url, name }));

        setSignedAttachments(resolved);

        if (resolved.length === 0) {
          setAttachmentError(
            "No attachment links could be generated. The files may have been removed from storage."
          );
        } else if (resolved.length < paths.length) {
          setAttachmentError(
            `Generated ${resolved.length} of ${paths.length} attachment links. Some files could not be located in storage.`
          );
        }
      } catch (err: any) {
        console.error("Failed to sign attachment URLs", err);
        if (!cancelled) {
          setSignedAttachments([]);
          setAttachmentError(
            err?.message
              ? `Could not generate download links: ${err.message}`
              : "Could not generate download links for this lead's attachments."
          );
        }
      } finally {
        if (!cancelled) setAttachmentsLoading(false);
      }
    };
    loadAttachments();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLead?.id, retryToken]);

  const retryAttachments = useCallback(() => {
    setRetryToken((t) => t + 1);
  }, []);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowRight") {
        setLightboxIndex((i) =>
          i === null ? null : (i + 1) % signedAttachments.length
        );
      }
      if (e.key === "ArrowLeft") {
        setLightboxIndex((i) =>
          i === null
            ? null
            : (i - 1 + signedAttachments.length) % signedAttachments.length
        );
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, signedAttachments.length]);

  const fetchLeads = async () => {
    try {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLeads(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch leads",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await supabase.from("leads").delete().eq("id", deleteId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Lead deleted successfully",
      });
      fetchLeads();
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete lead",
        variant: "destructive",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const exportToCSV = () => {
    const headers = ["Date", "Name", "Email", "Phone", "Service", "Location", "Boat Name", "Make/Model", "Length", "Message", "Source", "Status"];
    const rows = leads.map(lead => [
      new Date(lead.created_at).toLocaleDateString(),
      `${lead.first_name} ${lead.last_name}`,
      lead.email,
      lead.phone,
      lead.primary_service,
      lead.boat_location,
      lead.boat_name || "",
      lead.boat_make_model || "",
      lead.vessel_length || "",
      lead.message || "",
      lead.hear_about,
      lead.webhook_status,
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const currentLightbox =
    lightboxIndex !== null ? signedAttachments[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin/dashboard")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-foreground">Manage Leads</h1>
          </div>
          <Button onClick={exportToCSV} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Lead Submissions</CardTitle>
            <CardDescription>
              View and manage customer inquiries and quote requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : leads.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No leads found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <TableRow key={lead.id}>
                        <TableCell className="whitespace-nowrap">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="font-medium">
                          {lead.first_name} {lead.last_name}
                        </TableCell>
                        <TableCell>{lead.email}</TableCell>
                        <TableCell>{lead.phone}</TableCell>
                        <TableCell>{lead.primary_service}</TableCell>
                        <TableCell>
                          <Badge variant={lead.webhook_status === "sent" ? "default" : "secondary"}>
                            {lead.webhook_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => setSelectedLead(lead)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setDeleteId(lead.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Lead Details Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
            <DialogDescription>
              Submitted on {selectedLead && new Date(selectedLead.created_at).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Name</h4>
                  <p>{selectedLead.first_name} {selectedLead.last_name}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Email</h4>
                  <p>{selectedLead.email}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Phone</h4>
                  <p>{selectedLead.phone}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Service</h4>
                  <p>{selectedLead.primary_service}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Location</h4>
                  <p>{selectedLead.boat_location}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Source</h4>
                  <p>{selectedLead.hear_about}</p>
                </div>
                {selectedLead.boat_name && (
                  <div>
                    <h4 className="font-semibold text-sm text-muted-foreground mb-1">Boat Name</h4>
                    <p>{selectedLead.boat_name}</p>
                  </div>
                )}
                {selectedLead.boat_make_model && (
                  <div>
                    <h4 className="font-semibold text-sm text-muted-foreground mb-1">Make/Model</h4>
                    <p>{selectedLead.boat_make_model}</p>
                  </div>
                )}
                {selectedLead.vessel_length && (
                  <div>
                    <h4 className="font-semibold text-sm text-muted-foreground mb-1">Vessel Length</h4>
                    <p>{selectedLead.vessel_length} ft</p>
                  </div>
                )}
              </div>
              {selectedLead.message && (
                <div>
                  <h4 className="font-semibold text-sm text-muted-foreground mb-1">Message</h4>
                  <p className="whitespace-pre-wrap">{selectedLead.message}</p>
                </div>
              )}
              {(() => {
                const paths = normalizeFileUrls(selectedLead.file_urls);
                if (paths.length === 0) return null;
                return (
                  <div>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <h4 className="font-semibold text-sm text-muted-foreground">
                        Attachments ({paths.length})
                      </h4>
                      <div className="flex items-center gap-2">
                        {attachmentError && !attachmentsLoading && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={retryAttachments}
                            aria-label="Retry loading attachments"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Retry
                          </Button>
                        )}
                        {signedAttachments.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              signedAttachments.forEach((a) => {
                                window.open(a.url, "_blank", "noopener,noreferrer");
                              });
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Open all
                          </Button>
                        )}
                      </div>
                    </div>

                    {attachmentsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading attachments…
                      </div>
                    ) : (
                      <>
                        {attachmentError && (
                          <Alert variant="destructive" className="mb-3">
                            <AlertCircle className="h-4 w-4" />
                            <AlertTitle>Attachment issue</AlertTitle>
                            <AlertDescription className="break-words">
                              {attachmentError}
                            </AlertDescription>
                          </Alert>
                        )}

                        {signedAttachments.length > 0 && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {signedAttachments.map((att, idx) => (
                              <button
                                type="button"
                                key={att.path}
                                onClick={() => setLightboxIndex(idx)}
                                className="relative aspect-square rounded-lg overflow-hidden border border-border hover:border-primary transition-colors group block text-left"
                                title={att.name}
                                aria-label={`Open attachment ${idx + 1}: ${att.name}`}
                              >
                                <img
                                  src={att.url}
                                  alt={`Attachment ${idx + 1}: ${att.name}`}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Eye className="w-6 h-6 text-white" />
                                </div>
                                <span className="absolute bottom-0 left-0 right-0 text-[10px] px-1.5 py-1 bg-black/60 text-white truncate">
                                  {att.name}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox viewer */}
      {currentLightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 animate-in fade-in"
          role="dialog"
          aria-modal="true"
          aria-label="Attachment viewer"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(null);
            }}
            className="absolute top-4 right-4 z-10 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 transition-colors"
            aria-label="Close viewer"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Counter + filename */}
          <div className="absolute top-4 left-4 z-10 text-white text-sm bg-black/40 rounded-md px-3 py-1.5 max-w-[60vw] truncate">
            {(lightboxIndex ?? 0) + 1} / {signedAttachments.length} ·{" "}
            {currentLightbox.name}
          </div>

          {/* Prev */}
          {signedAttachments.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((i) =>
                  i === null
                    ? null
                    : (i - 1 + signedAttachments.length) %
                      signedAttachments.length
                );
              }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/10 hover:bg-white/20 text-white p-3 transition-colors"
              aria-label="Previous attachment"
            >
              <ChevronLeft className="w-7 h-7" />
            </button>
          )}

          {/* Image */}
          <img
            src={currentLightbox.url}
            alt={currentLightbox.name}
            className="max-w-full max-h-full object-contain select-none"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          {signedAttachments.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((i) =>
                  i === null ? null : (i + 1) % signedAttachments.length
                );
              }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 rounded-full bg-white/10 hover:bg-white/20 text-white p-3 transition-colors"
              aria-label="Next attachment"
            >
              <ChevronRight className="w-7 h-7" />
            </button>
          )}

          {/* Download */}
          <a
            href={currentLightbox.url}
            target="_blank"
            rel="noopener noreferrer"
            download={currentLightbox.name}
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 rounded-md bg-white/10 hover:bg-white/20 text-white px-3 py-2 text-sm transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Open original
          </a>
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this lead. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Leads;
